'use client';

import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { EmptyState } from '../../molecules/EmptyState';
import { SheetPropsData } from '../../molecules/Sheets/Sheets.types';
import { SheetsStack } from '../../molecules/Sheets/SheetsStack';
import {
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from '../../molecules/Tabs/Tabs';
import { PackageList } from '../PackageList/PackageList';
import { ProductOverview } from '../ProductOverview/ProductOverview';
import { ReleaseList } from '../ReleaseList/ReleaseList';
import { ReviewList } from '../ReviewList';
import { WriteReviewModal } from '../WriteReviewModal';
import { emptyWriteReview } from '../WriteReviewModal/WriteReviewModal';
import { ProductTabsProps } from './ProductTabs.types';

export const ProductTabs = ({
  microcopy,
  overview,
  packages,
  releases,
  reviews,
  writeReviewModal,
  value,
  onOpenInUnityFromPackage,
  onValueChange,
  onAccordionChange,
  onHelpfulVotesReview,
  onEditReview,
  onHelpfulVotesReply,
  onEditReply,
  onDeleteReply,
  onSubmitReply,
  onFilter,
  onSort,
  onPageChange,
  onSubmitReport,
  onFilterReviews,
  onSubmitReview,
  onEmailPublisher,
  onDeleteReview,
  onClickEmptyStateCta,
  className = '',
}: ProductTabsProps) => {
  const reviewListProps =
    reviews && microcopy.reviews
      ? {
          reviewStars: reviews.reviewStars,
          canWriteReview: reviews.canWriteReview,
          reviewList: reviews.reviewList,
          microcopy: microcopy.reviews,
          reviews: reviews.reviews,
          activeFilterValue: reviews.activeFilterValue,
          onHelpfulVotesReview,
          onEditReview,
          onHelpfulVotesReply,
          onEditReply,
          onDeleteReply,
          onSubmitReply,
          onFilter,
          onSort,
          onPageChange,
          onSubmitReport,
          onFilterReviews,
          onDeleteReview,
          onSubmitReview,
          onEmailPublisher,
          start: reviews.start,
          end: reviews.end,
          total: reviews.total,
          currentPage: reviews.currentPage,
          totalPages: reviews.totalPages,
          writeReview: {
            imageUrl: reviews.writeReviewModal.imageUrl,
            title: reviews.writeReviewModal.title,
            ownedDate: reviews.writeReviewModal.ownedDate,
            version: reviews.writeReviewModal.version,
          },
          className: 'mt-8 md:mt-0',
        }
      : null;

  const shouldShowReviewList =
    reviewListProps !== null &&
    ((reviews?.reviewStars?.reviews ?? 0) > 0 || reviews?.activeFilterValue);

  const mobileSheetDataWrapper: SheetPropsData[] = [
    {
      label: microcopy.tabs.overview,
      children: (
        <ProductOverview {...overview} microcopy={microcopy.overview} />
      ),
    },
    {
      label: microcopy.tabs.packages,
      children: (
        <PackageList
          {...packages}
          microcopy={microcopy.packages}
          onOpenInUnityFromPackage={onOpenInUnityFromPackage}
          onAccordionChange={onAccordionChange}
        />
      ),
    },
    {
      label: microcopy.tabs.releases,
      children: <ReleaseList {...releases} microcopy={microcopy.releases} />,
    },
    ...(shouldShowReviewList
      ? [
          {
            label: microcopy.tabs.reviews,
            children: (
              <ReviewList
                ownedDate={reviews?.ownedDate || ''}
                writeReviewModal={{
                  productId: writeReviewModal?.productId || '',
                  title: writeReviewModal?.title || '',
                  imageUrl: writeReviewModal?.imageUrl || '',
                  version: writeReviewModal?.version || '',
                  ownedDate: writeReviewModal?.ownedDate || '',
                  data: writeReviewModal?.data || undefined,
                  isMultiPackage: writeReviewModal?.isMultiPackage || false,
                }}
                {...reviewListProps}
              />
            ),
          },
        ]
      : []),
  ];

  const renderEmptyState = (
    <EmptyState
      microcopy={{
        title: microcopy?.emptyState?.title || '',
        description: microcopy?.emptyState?.description || '',
        cta: microcopy?.emptyState?.cta || '',
      }}
      onClick={onClickEmptyStateCta || (() => {})}
      modalTrigger={
        <WriteReviewModal
          productId={writeReviewModal?.productId || ''}
          title={writeReviewModal?.title || ''}
          imageUrl={writeReviewModal?.imageUrl || ''}
          version={writeReviewModal?.version || ''}
          ownedDate={writeReviewModal?.ownedDate || ''}
          microcopy={microcopy.writeReviewModal || emptyWriteReview}
          isMultiPackage={writeReviewModal?.isMultiPackage ?? false}
          onEmailPublisher={onEmailPublisher}
          onSubmitReview={onSubmitReview}
          reviewModalTrigger={
            <ButtonV2
              shape="square"
              size="small"
              label={microcopy.emptyState?.cta || ''}
            />
          }
        />
      }
    />
  );

  return (
    <section className={c('w-full', className)} id="product-tabs">
      <section className="hidden md:block">
        <TabsRoot value={value} onValueChange={onValueChange}>
          <TabsList>
            <TabsTrigger value="overview">
              {microcopy.tabs.overview}
            </TabsTrigger>
            <TabsTrigger value="packages">
              {microcopy.tabs.packages}
            </TabsTrigger>
            <TabsTrigger value="releases">
              {microcopy.tabs.releases}
            </TabsTrigger>
            {reviews ? (
              <TabsTrigger value="reviews">
                {microcopy.tabs.reviews}
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="overview" forceMount={true}>
            <ProductOverview {...overview} microcopy={microcopy.overview} />
          </TabsContent>
          <TabsContent value="packages" forceMount={true}>
            <PackageList
              {...packages}
              microcopy={microcopy.packages}
              onOpenInUnityFromPackage={onOpenInUnityFromPackage}
              onAccordionChange={onAccordionChange}
            />
          </TabsContent>
          <TabsContent value="releases" forceMount={true}>
            <ReleaseList
              {...releases}
              microcopy={microcopy.releases}
              onAccordionChange={onAccordionChange}
            />
          </TabsContent>
          {microcopy.reviews ? (
            <TabsContent value="reviews" forceMount={true}>
              {shouldShowReviewList ? (
                <ReviewList
                  ownedDate={reviews?.ownedDate || ''}
                  writeReviewModal={{
                    productId: reviews?.writeReviewModal?.productId || '',
                    title: reviews?.writeReviewModal?.title || '',
                    imageUrl: reviews?.writeReviewModal?.imageUrl || '',
                    version: reviews?.writeReviewModal?.version || '',
                    ownedDate: reviews?.writeReviewModal?.ownedDate || '',
                    data: reviews?.writeReviewModal?.data || undefined,
                    isMultiPackage:
                      reviews?.writeReviewModal?.isMultiPackage || false,
                  }}
                  {...reviewListProps}
                />
              ) : (
                renderEmptyState
              )}
            </TabsContent>
          ) : null}
        </TabsRoot>
      </section>

      <section className="block md:hidden">
        <SheetsStack
          sheets={mobileSheetDataWrapper}
          microcopy={microcopy.sheet}
          className="pt-0"
        />
      </section>
    </section>
  );
};
